import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import { fetchUpcomingEvents } from '../services/calendarService';
import usePageTitle from '../hooks/usePageTitle';

function Home() {
  usePageTitle(null);
  const [nextEvent, setNextEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEvents = async () => {
      const events = await fetchUpcomingEvents(1);
      if (events.length > 0) {
        setNextEvent(events[0]);
      }
      setLoading(false);
    };
    loadEvents();
  }, []);

  const getEventDateParts = (event) => {
    if (!event) return { month: 'TBD', day: '--', year: '----' };
    
    const dateString = event.start.dateTime || event.start.date;
    const date = new Date(dateString);
    
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      day: date.getDate(),
      year: date.getFullYear()
    };
  };

  const formatEventTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getEventTime = (event) => {
    if (!event || !event.start.dateTime) return 'All Day';
    
    const startTime = formatEventTime(event.start.dateTime);
    
    if (event.end && event.end.dateTime) {
      const endTime = formatEventTime(event.end.dateTime);
      return `${startTime} - ${endTime}`;
    }
    
    return startTime;
  };

  const getEventLocation = (event) => {
    if (!event || !event.location) return 'Muncy Lodge Hall, 28 Brummer Pond Rd, Muncy, PA';
    return event.location;
  };

  const dateParts = getEventDateParts(nextEvent);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-image" style={{
          backgroundImage: 'url(/droneshotx.jpg)',
          backgroundPosition: 'center 60%',
          backgroundSize: 'cover'
        }}></div>
        <div className="hero-overlay">
          <div className="hero-content">
            <div className="hero-title-section">
              <img 
                src="/pa-masons-logo.svg" 
                alt="Pennsylvania Masons" 
                className="hero-logo"
              />
              <h1>Muncy Lodge No. 299 Free and Accepted Masons</h1>
            </div>
            <p className="hero-subtitle">Building Better Men for Our Community</p>
            <p className="hero-tagline">The masonic lodge serving the Muncy, Hughesville, Clarkstown, Montgomery and surrounding Pennsylvania area.</p>
          </div>
        </div>
      </section>

      {/* Upcoming Event Section */}
      <section className="upcoming-event-section">
        <div className="container">
          <div className="event-card">
            <h2>Upcoming Event</h2>
            {loading ? (
              <p style={{ textAlign: 'center', padding: '2rem' }}>Loading event...</p>
            ) : nextEvent ? (
              <div className="event-details">
                <div className="event-date">
                  <span className="month">{dateParts.month}</span>
                  <span className="day">{dateParts.day}</span>
                  <span className="year">{dateParts.year}</span>
                </div>
                <div className="event-info">
                  <h3>{nextEvent.summary || 'Event'}</h3>
                  <p className="event-time">{getEventTime(nextEvent)}</p>
                  <p className="event-location">{getEventLocation(nextEvent)}</p>
                  <Link to="/calendar" className="event-link">View Full Calendar →</Link>
                </div>
              </div>
            ) : (
              <div className="event-details">
                <p style={{ textAlign: 'center', padding: '2rem' }}>No upcoming events scheduled. Check back soon!</p>
                <Link to="/calendar" className="event-link" style={{ textAlign: 'center', display: 'block' }}>View Full Calendar →</Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Three Tenants Section */}
      <section className="three-tenants-section">
        <div className="container">
          <h2>The Three Tenants</h2>
          <div className="tenants-grid">
            <div className="tenant-card">
              <div className="tenant-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </div>
              <h3>Brotherly Love</h3>
              <p>We practice mutual respect, care, and support for one another, treating all men as brothers regardless of their background or station in life.</p>
            </div>

            <div className="tenant-card">
              <div className="tenant-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" fill="currentColor">
                  <path d="M323.4 85.2l-96.8 78.4c-16.1 13-19.2 36.4-7 53.1c12.9 17.8 38 21.3 55.3 7.8l99.3-77.2c7-5.4 17-4.2 22.5 2.8s4.2 17-2.8 22.5l-20.9 16.2L512 316.8V128h-.7l-3.9-2.5L434.8 79c-15.3-9.8-33.2-15-51.4-15c-21.8 0-43 7.5-60 21.2zm22.8 124.4l-51.7 40.2C263 274.4 217.3 268 193.7 235.6c-22.2-30.5-16.6-73.1 12.7-96.8l83.2-67.3c-11.6-4.9-24.1-7.4-36.8-7.4C234 64 215.7 69.6 200 80l-72 48V352h28.2l91.4 83.4c19.6 17.9 49.9 16.5 67.8-3.1c5.5-6.1 9.2-13.2 11.1-20.6l17 15.6c19.5 17.9 49.9 16.6 67.8-2.9c4.5-4.9 7.8-10.6 9.9-16.5c19.4 13 45.8 10.3 62.1-7.5c17.9-19.5 16.6-49.9-2.9-67.8l-134.2-123zM16 128c-8.8 0-16 7.2-16 16V352c0 17.7 14.3 32 32 32H64c17.7 0 32-14.3 32-32V128H16zM48 320a16 16 0 1 1 0 32 16 16 0 1 1 0-32zM544 128V352c0 17.7 14.3 32 32 32h32c17.7 0 32-14.3 32-32V144c0-8.8-7.2-16-16-16H544zm32 208a16 16 0 1 1 32 0 16 16 0 1 1 -32 0z"/>
                </svg>
              </div>
              <h3>Relief</h3>
              <p>We extend charity and assistance to those in need, supporting our community through acts of kindness and service to others.</p>
            </div>

            <div className="tenant-card">
              <div className="tenant-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h3>Truth</h3>
              <p>We seek knowledge and wisdom, striving to live with integrity, honesty, and moral uprightness in all our dealings.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Interested in Joining?</h2>
            <p>There are several ways to begin your Masonic journey with Muncy Lodge No. 299:</p>
            <ul className="join-options">
              <li><strong>Ask a Current Member:</strong> If you know a Mason, reach out and express your interest</li>
              <li><strong>Attend Our Public Events:</strong> <Link to="/calendar">Check our calendar</Link> and follow us on <a href="https://www.facebook.com/muncylodge299" target="_blank" rel="noopener noreferrer">Facebook</a> for upcoming open events</li>
              <li><strong>Contact Us Directly:</strong> Visit our <Link to="/contact">contact page</Link> to get in touch</li>
              <li><strong>Learn More & Submit a Petition:</strong> Visit the <a href="https://pagrandlodge.org/join/" target="_blank" rel="noopener noreferrer">PA Grand Lodge website</a> for detailed information and to submit your petition</li>
            </ul>
            <div className="cta-buttons">
              <a href="https://pagrandlodge.org/join/" target="_blank" rel="noopener noreferrer" className="cta-button primary">
                How to Join (PA Grand Lodge) →
              </a>
              <a href="https://pagrandlodge.org/masonic-frequently-asked-questions/" target="_blank" rel="noopener noreferrer" className="cta-button secondary">
                Learn More About Freemasonry (PA Grand Lodge) →
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
